import { useEffect } from "react"
import { Button } from "@alascorporation/ui"
import { useSidebar } from "../../context/sidebar"

export type RecordSaveStatus = "idle" | "saving" | "saved"

const STATUS_LABEL: Record<RecordSaveStatus, string> = {
  idle: "Sin cambios",
  saving: "Guardando cambios...",
  saved: "Todos los cambios guardados",
}

const STATUS_DOT_CLASS: Record<RecordSaveStatus, string> = {
  idle: "bg-gray-300 dark:bg-gray-600",
  saving: "animate-pulse bg-warning-500",
  saved: "bg-success-500",
}

/**
 * Barra fija (`position: fixed`, no `sticky`) al pie de la ventana — permanece visible sin
 * importar el scroll, alineada al ancho del contenido según el estado del sidebar principal.
 * Al estar fuera del flujo, quien la usa DEBE reservar espacio debajo de su último campo
 * (ej. `pb-20` en el formulario) para que no tape contenido.
 *
 * Refleja el guardado optimista por campo ya vigente (manager-optimistic-mutations) — no es
 * un modo de guardado nuevo. `onForceSync` es opcional: en shells sin "cambios sin guardar"
 * que enviar puede ser un no-op o disparar un refetch, nunca un submit de formulario completo.
 */
export function RecordSaveStatusBar({
  status,
  onForceSync,
}: {
  status: RecordSaveStatus
  onForceSync?: () => void
}) {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar()

  useEffect(() => {
    if (!onForceSync) return
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault()
        onForceSync!()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [onForceSync])

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-border bg-card px-4 py-3 transition-all duration-300 ease-in-out ${
        isExpanded || isHovered ? "lg:left-[290px]" : "lg:left-[90px]"
      } ${isMobileOpen ? "left-0" : ""}`}
    >
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <span className={`size-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[status]}`} aria-hidden="true" />
        {STATUS_LABEL[status]}
      </div>
      {onForceSync ? (
        <Button type="button" size="sm" onClick={onForceSync}>
          Guardar
          <kbd className="ml-2 rounded border border-white/30 px-1 text-[11px] font-normal opacity-80">
            Ctrl+S
          </kbd>
        </Button>
      ) : null}
    </div>
  )
}
